import { correctionAngleForBoundingBox, vert3OffsetsForBoundingBox } from "barcodes/bounding-box";
import type { PossibleBarcode } from "barcodes/chunk-clustering";
import type { DecodeResult } from "barcodes/decoder";
import { ColorSpace } from "barcodes/locator/color-spaces";
import { BarcodeWorkerClient, BarcodeWorkerPool } from "barcodes/worker/client";
import { CodeScannerStandard } from "common/code-scanner-standards";
import type { AppKind } from "common/components/app-kind";
import { getAnyLocalizedString } from "common/components/localization";
import { logError } from "common/debug-print";
import { fitBoxIntoBox, rotatePointAbout, xAtYIntercept } from "common/geometry";
import { dontAwait } from "common/Support";
import * as React from "react";
import ActivitySpinner from "webapp/chrome/views/activity-spinner/activity-spinner";
import { useRefState } from "webapp/lib/hooks/refs";
// eslint-disable-next-line import/no-unresolved
import Worker from "worker-loader!./decode.worker";
import {
    codeDetectedLineColor,
    codeFoundLineColor,
    codeFoundLineThickness,
    codeFoundShadeColor,
    LiveLinearBarcodeScannerContainer,
    noCodeFoundColor,
    noCodeFoundLineThickness,
} from "./live-linear-barcode-scanner-style";

type Props = Readonly<{
    appKind: AppKind;
    onScannedCode: ((standard: CodeScannerStandard, code: string) => void) | undefined;
}>;

function mkworker(workerID: number): Worker {
    const worker = new Worker();
    worker.postMessage({ kind: "locator-bootstrap", workerID });
    return worker;
}

function logPoolError(e: ErrorEvent) {
    logError("In BarcodeWorkerPool", e);
}

const defaultChunkDimension = 32;
const outputPadding = 16;

function chunkDimensionForPortraitHeight(h: number): number {
    if (h <= 320) {
        return 24;
    } else if (h <= 512) {
        return 32;
    } else {
        return 48;
    }
}

function chunkDimensionForLandscapeWidth(w: number): number {
    if (w <= 512) {
        return 24;
    } else if (w <= 1280) {
        return 32;
    } else if (w <= 1920) {
        return 64;
    } else {
        return 96;
    }
}

const clusterParams = {
    // This is just about 5 degrees
    zeroEpsilon: 0.0873,
    // This is just about 8 degrees
    angleEpsilon: 0.14,
    minFactor: 4,
    maxFactor: 1.3,
    minClusterSize: 3,
    distanceSinAllowance: 0.8,
};

const decodeParams = {
    maxQuantile: 0.85,
    errorAtMaxQuantile: 0.15,
    decodeStep: 3,
    xPadding: outputPadding,
};

const overlapParams = {
    areaFactor: 0.25,
    maxAngleDelta: 0.14,
};

interface ControlVars {
    visible: boolean;
    currentlyRunning: boolean;
    lastTime: number;

    foundCodes: boolean;
    lastCode: DecodeResult | undefined;
    consecutiveCodes: number;
    consecutiveErrors: number;
    chunkDimension: number;

    onScannedCode: Props["onScannedCode"];
}

const pollRate = 1000 / 8; // That's 8 fps
const valueThreshold = 3;

interface ProcessFrameArgs {
    cv: ControlVars;
    captureCtx: CanvasRenderingContext2D;
    container: HTMLDivElement;
    liveVideo: HTMLVideoElement;
    client: BarcodeWorkerClient;
}

function stopStream(s: MediaStream) {
    for (const track of s.getTracks()) {
        track.stop();
    }
}

function setStyleForContainer(container: HTMLDivElement, boundingRect: DOMRect, cv: ControlVars) {
    const orientationStyle = `--line-container-flex-grow: ${boundingRect.width < boundingRect.height ? 1 : 2}`;
    const shadeColorStyle = `--container-shade-background-color: ${
        cv.consecutiveCodes < valueThreshold ? noCodeFoundColor : codeFoundShadeColor
    }`;
    const lineThicknessStyle = `--container-line-thickness: ${
        cv.lastCode === undefined ? noCodeFoundLineThickness : codeFoundLineThickness
    }`;
    let lineColor = cv.lastCode === undefined ? noCodeFoundColor : codeFoundLineColor;
    if (cv.lastCode === undefined && cv.foundCodes) {
        lineColor = codeDetectedLineColor;
    }
    const lineColorStyle = `--container-line-background-color: ${lineColor}`;
    container.setAttribute("style", [orientationStyle, shadeColorStyle, lineColorStyle, lineThicknessStyle].join(";"));
}

const pixelLimit = 1024 * 1024;
function dimensionsForCanvas(
    width: number,
    height: number
): {
    displayWidth: number;
    displayHeight: number;
    drawWidth: number;
    drawHeight: number;
} {
    const pixelDensity = width * height;
    if (pixelDensity <= pixelLimit) {
        return {
            displayWidth: width,
            displayHeight: height,
            drawWidth: width,
            drawHeight: height,
        };
    }

    const aspectRatio = width / height;
    // We want to preserve the aspect ratio while at the same time ensuring
    // only a specific resolution. This gives us the following set of equations:

    // pixelLimit = newWidth * newHeight
    // newWidth / newHeight = width / height

    // Between these, we can solve for both newWidth and newHeight.
    // newWidth * newHeight * newWidth / newHeight
    // = pixelLimit * width / height
    // = newWidth * newWidth

    // newWidth = Math.sqrt(pixelLimit * width / height)

    // newWidth * newHeight * newHeight / newWidth
    // = pixelLimit * height / width
    // = newHeight * newHeight

    // newHeight = Math.sqrt(pixelLimit * height / width)

    const newWidth = Math.sqrt(pixelLimit * aspectRatio);
    const newHeight = Math.sqrt(pixelLimit / aspectRatio);
    return {
        displayWidth: Math.floor(newWidth),
        displayHeight: Math.floor(newHeight),
        drawWidth: newWidth,
        drawHeight: newHeight,
    };
}

async function processFrame(args: ProcessFrameArgs) {
    const { cv, client, container } = args;
    if (!cv.visible) {
        if (!cv.currentlyRunning) {
            client.params.pool.close();
        }
        return;
    }

    const { currentlyRunning, lastTime } = cv;
    const boundingClientRect = container.getBoundingClientRect();

    if (currentlyRunning || window.performance.now() - lastTime < pollRate) {
        setStyleForContainer(container, boundingClientRect, cv);
        requestAnimationFrame(() => dontAwait(processFrame(args)));
        return;
    }

    cv.currentlyRunning = true;
    try {
        const { liveVideo, captureCtx } = args;
        if (liveVideo.videoWidth === 0 || liveVideo.videoHeight === 0) return;

        const viewportFit = fitBoxIntoBox(boundingClientRect, {
            width: liveVideo.videoWidth,
            height: liveVideo.videoHeight,
        });
        const viewportAspectRatio = viewportFit.width / viewportFit.height;
        const scanHeight = Math.floor(viewportAspectRatio > 1 ? (viewportFit.height * 3) / 5 : viewportFit.height / 3);
        const scanFit = fitBoxIntoBox({ width: viewportFit.width, height: scanHeight }, viewportFit);
        const scanX = scanFit.x + viewportFit.x;
        const scanY = scanFit.y + viewportFit.y;

        const { canvas } = captureCtx;
        const { drawWidth, drawHeight, displayWidth, displayHeight } = dimensionsForCanvas(
            scanFit.width,
            scanFit.height
        );
        canvas.width = displayWidth;
        canvas.height = displayHeight;

        if (canvas.width === 0 || canvas.height === 0) return;

        const expectedChunkDim =
            viewportAspectRatio > 1
                ? chunkDimensionForLandscapeWidth(displayWidth)
                : chunkDimensionForPortraitHeight(displayHeight);
        if (expectedChunkDim !== cv.chunkDimension) {
            client.resetChunkDimension(expectedChunkDim);
            cv.chunkDimension = expectedChunkDim;
        }

        captureCtx.drawImage(liveVideo, scanX, scanY, scanFit.width, scanFit.height, 0, 0, drawWidth, drawHeight);

        const firstPassResult = await client.locateBarcodes(ColorSpace.Linear);
        if (!cv.visible) return;

        const yMid = scanFit.height / 2;
        const intersectingPossibleBarcodes: PossibleBarcode[] = [];
        const intersectingImageDatas: ImageData[] = [];

        for (let i = 0; i < firstPassResult.possibleBarcodes.length; i++) {
            const barcode = firstPassResult.possibleBarcodes[i];
            const imageData = firstPassResult.barcodesImageData[i];
            if (barcode === undefined || imageData === undefined) continue;

            const { boundingBox } = barcode;
            const { vertex1, vertex2, vertex3, vertex4 } = boundingBox;

            if (vertex1[1] > yMid && vertex2[1] > yMid && vertex3[1] < yMid && vertex4[1] < yMid) {
                intersectingPossibleBarcodes.push(barcode);
                intersectingImageDatas.push(imageData);
            } else if (vertex1[1] < yMid && vertex2[1] < yMid && vertex3[1] > yMid && vertex4[1] > yMid) {
                intersectingPossibleBarcodes.push(barcode);
                intersectingImageDatas.push(imageData);
            }
        }

        const mergedResult = client.mergeOverlappingPossibleBarcodes(
            intersectingPossibleBarcodes,
            intersectingImageDatas,
            overlapParams
        );

        const { possibleBarcodes, barcodesImageData } = mergedResult;

        cv.foundCodes = possibleBarcodes.length > 0;
        if (possibleBarcodes.length === 0) {
            if (++cv.consecutiveErrors > valueThreshold) {
                cv.consecutiveCodes = 0;
                if (cv.lastCode !== undefined && cv.onScannedCode !== undefined) {
                    cv.onScannedCode(CodeScannerStandard.None, "");
                }
                cv.lastCode = undefined;
            }
        } else {
            let decodeResult: DecodeResult | undefined;
            for (let i = 0; i < possibleBarcodes.length; i++) {
                const targetBarcode = possibleBarcodes[i];
                const imageData = barcodesImageData[i];
                if (targetBarcode === undefined || imageData === undefined) continue;

                const { boundingBox } = targetBarcode;
                const { vertex1, vertex2, vertex3, vertex4 } = boundingBox;
                const x14 = xAtYIntercept(vertex1[0], vertex1[1], vertex4[0], vertex4[1]);
                const x23 = xAtYIntercept(vertex2[0], vertex2[1], vertex3[0], vertex3[1]);
                const v3Offsets = vert3OffsetsForBoundingBox(boundingBox, outputPadding, outputPadding);
                const midscanPoint = rotatePointAbout(
                    (x14 + x23) / 2,
                    0,
                    correctionAngleForBoundingBox(boundingBox),
                    v3Offsets[0],
                    v3Offsets[1]
                );

                const nextDecodeResult = await client.decodeFromImageData(
                    imageData,
                    [ColorSpace.Linear, ColorSpace.Srgb],
                    decodeParams,
                    { yStart: Math.floor(midscanPoint[1]), yMin: 0, yMax: imageData.height }
                );

                if (!cv.visible) break;

                if (decodeResult === undefined) {
                    decodeResult = nextDecodeResult;
                } else if (nextDecodeResult !== undefined) {
                    // We can't (yet) have two valid barcodes intersecting on the same line.
                    // The only valid configurations for this would be EAN 5 or EAN 2 on the
                    // same line as EAN 13, because they're supersets of EAN 13. But we already
                    // don't handle EAN 5 or EAN 2 properly yet...
                    decodeResult = undefined;
                    break;
                }
            }

            if (!cv.visible) return;
            if (decodeResult === undefined) {
                if (++cv.consecutiveErrors > valueThreshold) {
                    cv.consecutiveCodes = 0;
                    if (cv.lastCode !== undefined && cv.onScannedCode !== undefined) {
                        cv.onScannedCode(CodeScannerStandard.None, "");
                    }
                    cv.lastCode = undefined;
                }
            } else if (
                cv.lastCode === undefined ||
                cv.lastCode.code !== decodeResult.code ||
                cv.lastCode.standard !== decodeResult.standard
            ) {
                cv.consecutiveCodes = 0;
                cv.consecutiveErrors = 0;
                cv.lastCode = decodeResult;
            } else {
                cv.consecutiveErrors = 0;
                if (++cv.consecutiveCodes === valueThreshold && cv.onScannedCode !== undefined) {
                    cv.onScannedCode(decodeResult.standard, decodeResult.code);
                }
            }
        }
    } catch (e: unknown) {
        logError("Processing linear barcode frame", e);
    } finally {
        cv.currentlyRunning = false;
        cv.lastTime = window.performance.now();
        if (cv.visible) {
            setStyleForContainer(container, container.getBoundingClientRect(), cv);
            requestAnimationFrame(() => dontAwait(processFrame(args)));
        } else {
            client.params.pool.close();
        }
    }
}

export function LiveLinearBarcodeScanner({ onScannedCode, appKind }: Props): JSX.Element {
    const [cameraStream, setCameraStream] = React.useState<MediaStream | false | undefined>(undefined);
    const captureCanvas = React.useMemo(() => document.createElement("canvas"), []);
    const rotateCanvas = React.useMemo(() => document.createElement("canvas"), []);

    const captureCtx = React.useMemo(() => captureCanvas.getContext("2d"), [captureCanvas]);
    const rotateCtx = React.useMemo(() => rotateCanvas.getContext("2d"), [rotateCanvas]);

    const [containerRef, containerElem] = useRefState<HTMLDivElement>();
    const [videoRef, videoElem] = useRefState<HTMLVideoElement>();

    const controlVars = React.useMemo<ControlVars>(
        () => ({
            visible: true,
            currentlyRunning: false,
            lastTime: window.performance.now(),
            lastCode: undefined,
            consecutiveCodes: 0,
            consecutiveErrors: 0,
            foundCodes: false,
            chunkDimension: defaultChunkDimension,
            onScannedCode: () => {
                /* Do nothing, we'll replace this below. */
            },
        }),
        []
    );
    controlVars.onScannedCode = onScannedCode;

    React.useEffect(() => {
        return () => {
            controlVars.visible = false;
        };
    }, [controlVars]);

    React.useEffect(() => {
        if (containerElem === undefined || cameraStream === false) return undefined;
        if (cameraStream !== undefined) {
            return () => stopStream(cameraStream);
        }

        if (navigator.mediaDevices === undefined) {
            setCameraStream(false);
            return undefined;
        }

        const { width, height } = containerElem.getBoundingClientRect();
        const pixelRatio = window.devicePixelRatio ?? 1;
        const unscaledWidth = width * pixelRatio;
        const unscaledHeight = height * pixelRatio;
        let mediaHeight: MediaTrackConstraintSet["height"] = { exact: unscaledHeight };

        if (unscaledWidth < 800 || unscaledHeight < 800) {
            mediaHeight = { ideal: 1500 };
        }

        navigator.mediaDevices
            .getUserMedia({
                video: {
                    facingMode: "environment",
                    height: mediaHeight,
                },
            })
            .then(s => {
                if (controlVars.visible) {
                    setCameraStream(s);
                } else {
                    stopStream(s);
                }
            })
            .catch(e => {
                logError("Setting up linear barcode scanner (exact)", e);

                // This happens sometimes: when we can't hit the exact dimensions
                // we have to downgrade to "ideal" dimensions.
                if (e instanceof OverconstrainedError) {
                    navigator.mediaDevices
                        .getUserMedia({
                            video: {
                                facingMode: "environment",
                                height: { ideal: unscaledHeight },
                            },
                        })
                        .then(s2 => {
                            if (controlVars.visible) {
                                setCameraStream(s2);
                            } else {
                                stopStream(s2);
                            }
                        })
                        .catch(e2 => {
                            logError("Setting up linear barcode scanner (ideal)", e2);
                            if (controlVars.visible) {
                                setCameraStream(false);
                            }
                        });
                    return;
                }
                if (controlVars.visible) {
                    setCameraStream(false);
                }
            });

        return undefined;
    }, [controlVars, containerElem, cameraStream, setCameraStream]);

    const client = React.useMemo(() => {
        if (captureCtx !== null && rotateCtx !== null && cameraStream !== false && cameraStream !== undefined) {
            return new BarcodeWorkerClient({
                pool: new BarcodeWorkerPool(Math.min(navigator.hardwareConcurrency ?? 2, 4), mkworker, logPoolError),
                chunkDimension: defaultChunkDimension,
                outputPadding,
                clusterParams,
                srcCtx: captureCtx,
                procCtx: rotateCtx,
            });
        } else {
            return null;
        }
    }, [captureCtx, rotateCtx, cameraStream]);

    React.useEffect(() => {
        if (videoElem !== undefined && cameraStream !== undefined && cameraStream !== false) {
            videoElem.srcObject = cameraStream;
        }
    }, [videoElem, cameraStream]);

    React.useEffect(() => {
        if (captureCtx !== null && containerElem !== undefined && videoElem !== undefined && client !== null) {
            dontAwait(
                processFrame({
                    cv: controlVars,
                    container: containerElem,
                    liveVideo: videoElem,
                    captureCtx,
                    client,
                })
            );
        }
    }, [captureCtx, containerElem, videoElem, client, controlVars, onScannedCode]);

    const outerStyle: React.CSSProperties = React.useMemo(
        () => ({
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
        }),
        []
    );

    let content: React.ReactNode;
    if (cameraStream === undefined) {
        content = <ActivitySpinner />;
    } else if (cameraStream === false) {
        // FIXME: Translate this before general release.
        content = (
            <div className="llbs-no-camera">
                <span>{getAnyLocalizedString("noCameraAccess", appKind)}</span>
            </div>
        );
    } else {
        content = (
            <>
                <div className="llbs-video-container">
                    <video className="llbs-video" playsInline={true} muted={true} autoPlay={true} ref={videoRef} />
                </div>
                <div className="llbs-bounding-container">
                    <div className="llbs-bounding-shade"></div>
                    <div className="llbs-bounding-line-container">
                        <div className="llbs-bounding-line"></div>
                    </div>
                    <div className="llbs-bounding-shade"></div>
                </div>
            </>
        );
    }

    return (
        <div style={outerStyle}>
            <LiveLinearBarcodeScannerContainer ref={containerRef}>{content}</LiveLinearBarcodeScannerContainer>
        </div>
    );
}
